import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { orgUnitTypesApi } from '../organizationApi';
import type { OrgUnitTypeListItem } from '../organizationTypes';
import { sampleOrgUnitType } from '../utils/sampleOrgUnit';

/**
 * Create or edit one tier of the chart's vocabulary.
 *
 * One modal for both because the fields are identical — the only difference is whether an
 * id is being written to, which is not a reason for a second form to drift away from this one.
 */
export function OrgUnitTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: OrgUnitTypeListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  /** Fills the one required field. The description is optional and left alone. */
  function autogenerate() {
    setName(sampleOrgUnitType().name);
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    // No order — the server appends a new tier and leaves an existing one where it is.
    const request = {
      name: name.trim(),
      description: description.trim() || null,
    };

    try {
      if (editing) await orgUnitTypesApi.update(editing.id, request);
      else await orgUnitTypesApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the tier.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, { idPrefix: 'org-unit-type' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit tier' : 'New tier'}
      size="md"
      id="org-unit-type-modal"
      // Create only: on an edit this would overwrite a tier the chart already sorts by.
      autogenerate={
        editing === null ? { onGenerate: autogenerate, disabled: saving } : undefined
      }
      confirm={{
        id: 'org-unit-type-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="org-unit-type-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="org-unit-type-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          Read-only, and only when editing.

          A level is a position among the other tiers, so it cannot be typed here: setting one
          says nothing about what happens to the tier already at that level. It is shown
          because a reader editing a tier needs to know where it sits, and it is locked
          because moving it is its own action — "Move level" on the row.

          Absent on a create: a new tier is appended, so there is no level to state yet.
        */}
        {editing ? (
          <TextField
            id="org-unit-type-level"
            label="Level"
            value={String(editing.level)}
            onChange={() => undefined}
            readOnly
            disabled
            description="Where this tier sits on the ladder. Change it with Move level on the tier's own row — a level is a position, so it is always moved against another tier rather than typed."
          />
        ) : null}

        <TextField
          id="org-unit-type-name"
          label="Name"
          required
          description="The word this organization uses for the tier — Division, Department, Section. Unique among active types."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={150}
          disabled={saving}
        />

        <TextField
          id="org-unit-type-description"
          label="Description"
          description="Optional note shown in the catalog list."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
